import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { useDrawer } from "@/components/RightDrawer";
import Avatar, { STATUS_COLORS, STATUS_LABELS } from "@/components/Avatar";
import { isOnline as isEffectivelyOnline } from "@/lib/presence";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Video, Mic, MessageCircle, Users, Sparkles, ZoomIn, ZoomOut, Home, Settings2 } from "lucide-react";
import { toast } from "sonner";
import {
  worldToScreen, screenToWorld, isoCanvasFor, rotateInputForIso,
  WALL_HEIGHT_PX, ISO_SCALE_X, ISO_SCALE_Y,
} from "@/lib/iso";

// World-space (game units). Expanded to 1600×1000 so teams of 20+ have
// room to spread out across the office instead of clustering on top of
// each other. Iso projection maps this to a ~2210×1300 canvas.
const MAP_W = 1600;
const MAP_H = 1000;
const { canvasW: ISO_CANVAS_W, canvasH: ISO_CANVAS_H, offsetX: ISO_OFFSET_X } =
  isoCanvasFor(MAP_W, MAP_H);

// Playful/cartoony room palette — chosen for warmth + high contrast on
// pastel floors. Wall shade is the same hue at reduced lightness.
const ROOM_THEME = {
  workspace:  { floor: "#FFF3E0", wall: "#F1BE85", accent: "#F97316" },
  meeting:    { floor: "#E0F2FE", wall: "#7DD3FC", accent: "#0284C7" },
  focus:      { floor: "#EDE9FE", wall: "#C4B5FD", accent: "#7C3AED" },
  reception:  { floor: "#FEF3C7", wall: "#FCD34D", accent: "#D97706" },
  breakout:   { floor: "#FCE7F3", wall: "#F9A8D4", accent: "#DB2777" },
  lounge:     { floor: "#DCFCE7", wall: "#86EFAC", accent: "#059669" },
  ops:        { floor: "#F1F5F9", wall: "#94A3B8", accent: "#334155" },
};

// Map backend `type` values to legacy RoomTile `kind` used for icons/palette.
const TYPE_TO_KIND = {
  open_workspace: "workspace",
  department: "workspace",
  meeting: "meeting",
  focus: "focus",
  reception: "reception",
  break: "breakout",
  lounge: "lounge",
  project: "meeting",
  training: "meeting",
  event: "meeting",
  private: "workspace",
  custom: "ops",
};

/**
 * Convert a layout doc {grid:{cell_px}, rooms:[{x,y,w,h,type,color,name,id}]}
 * into pixel-space rooms used by the render + roomAt() lookup.
 *
 * Auto-scales rooms proportionally to fill the (bigger) MAP_W × MAP_H
 * canvas — existing layouts were authored against a 1000×640 world, but
 * we now render at 1600×1000. Also injects a default door on the SOUTH
 * wall of each room (60px wide, centered) so wall collision has a
 * legit way in/out.
 */
function layoutToRooms(layout) {
  if (!layout || !Array.isArray(layout.rooms) || !layout.rooms.length) return [];
  const cell = layout.grid?.cell_px || 10;
  // First pass: naive scale-by-cell.
  const raw = layout.rooms.map((r) => ({
    id: r.id, name: r.name, type: r.type, department: r.department,
    seats: r.seats || [], seat_count: r.seat_count,
    floor: r.color || "#F7F7F5",
    kind: TYPE_TO_KIND[r.type] || "workspace",
    door: r.door, // may be provided by future layouts
    x: (r.x || 0) * cell,
    y: (r.y || 0) * cell,
    w: (r.w || 0) * cell,
    h: (r.h || 0) * cell,
  }));
  // Bounding box of the layout (pre-scaling).
  const maxX = Math.max(...raw.map((r) => r.x + r.w));
  const maxY = Math.max(...raw.map((r) => r.y + r.h));
  // Fit into the map with a 40px margin on all sides.
  const targetW = MAP_W - 80;
  const targetH = MAP_H - 80;
  const sx = maxX > 0 ? targetW / maxX : 1;
  const sy = maxY > 0 ? targetH / maxY : 1;
  const s = Math.min(sx, sy);
  return raw.map((r) => {
    const scaled = {
      ...r,
      x: 40 + r.x * s,
      y: 40 + r.y * s,
      w: r.w * s,
      h: r.h * s,
    };
    // Auto-door on the SOUTH wall if not specified.
    scaled.door = r.door || { side: "S", width: 80 };
    return scaled;
  });
}

function roomAt(rooms, x, y) {
  for (const r of rooms) if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) return r;
  return null;
}
function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
function distance(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

export default function OfficeMap({ presence, wsSend, subscribe, connected, onOpenCall, onOpenPeople }) {
  const { user, currentOrg, memberships } = useAuth();
  const { openDrawer, closeDrawer } = useDrawer();
  const mapRef = useRef(null);
  // Seeded once from the server-side presence when the component mounts so
  // navigating away and back doesn't teleport the user home. Initial value is
  // null → the "have not initialized yet" sentinel.
  const [myPos, setMyPos] = useState(null);
  const hasUserMoved = useRef(false);
  // Walking animation + sit-at-desk state
  const [isMoving, setIsMoving] = useState(false);
  // sittingAt = null | { furnitureId, x, y, emoji } — freezes movement,
  // enables the breathing idle animation.
  const [sittingAt, setSittingAt] = useState(null);
  const [hoverUser, setHoverUser] = useState(null);
  const [members, setMembers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [nearbyPrompt, setNearbyPrompt] = useState(null);
  const dismissedNearby = useRef(new Set());
  // Floating reactions state — each entry {id, user_id, emoji}, auto-removed after 2.6s
  const [reactions, setReactions] = useState([]);
  const lastReactionAtRef = useRef(0);

  const sendReaction = (emoji) => {
    if (!wsSend) return;
    const now = Date.now();
    if (now - lastReactionAtRef.current < 500) return; // client-side rate limit
    lastReactionAtRef.current = now;
    wsSend({ type: "office.reaction", emoji });
  };

  useEffect(() => {
    api.get("/orgs/current/members").then((r) => {
      // Normalize membership rows into a flat shape convenient for the
      // Assign popover + heldBy lookup: `.id` becomes the USER id (not
      // the membership id) and the nested `user` fields (name, email,
      // avatar) get promoted. We keep `user_id` untouched so existing
      // consumers (visibleUsers builder) keep working.
      const rows = r.data || [];
      setMembers(rows.map((m) => ({
        ...m,
        id: m.user_id,
        name: m.user?.name,
        email: m.user?.email,
        avatar_url: m.user?.avatar_url,
        avatar_type: m.user?.avatar_type,
        avatar_seed: m.user?.avatar_seed,
        avatar_config: m.user?.avatar_config,
        profile_photo_url: m.user?.profile_photo_url,
        title: m.user?.title,
      })));
    });
    api.get("/office/layout")
      .then((r) => setRooms(layoutToRooms(r.data)))
      .catch(() => setRooms([]));
  }, []);

  // Seed my position from the *server-side* presence the first time we see it
  // for the current user. Prevents the "avatar always snaps back to Open
  // Workspace after visiting Home or a channel" bug — the OfficeMap component
  // unmounts on navigation and would otherwise re-mount with a default (220,
  // 260) that lives inside Open Workspace and then broadcast that position
  // over the WS, clobbering the real one.
  useEffect(() => {
    if (myPos || !user) return;
    const p = presence?.[user.id];
    if (p && typeof p.x === "number" && typeof p.y === "number") {
      setMyPos({ x: p.x, y: p.y });
    } else {
      // No known position yet — start at a neutral spawn (only used the very
      // first time the user ever visits the office).
      setMyPos({ x: 220, y: 260 });
    }
  }, [presence, user, myPos]);

  // Refresh members list when a user profile changes (avatar/name/status) so
  // any subsequent renders pull fresh data even for users not currently in presence.
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type, payload) => {
      if (type === "user.updated") {
        api.get("/orgs/current/members").then((r) => {
          const rows = r.data || [];
          setMembers(rows.map((m) => ({
            ...m,
            id: m.user_id,
            name: m.user?.name,
            email: m.user?.email,
            avatar_url: m.user?.avatar_url,
            avatar_type: m.user?.avatar_type,
            avatar_seed: m.user?.avatar_seed,
            avatar_config: m.user?.avatar_config,
            profile_photo_url: m.user?.profile_photo_url,
            title: m.user?.title,
          })));
        }).catch(() => {});
      }
      if (type === "office.published" || type === "office.restored") {
        api.get("/office/layout").then((r) => setRooms(layoutToRooms(r.data))).catch(() => {});
      }
      if (type === "office.seat_claimed" || type === "office.seat_released" || type === "office.seat_assigned") {
        // A teammate just claimed/released/was-assigned a desk — re-fetch
        // the layout so the name chip + manager-lock indicator updates
        // for everyone in real time.
        api.get("/office/layout").then((r) => setRooms(layoutToRooms(r.data))).catch(() => {});
      }
      if (type === "office.reaction" && payload?.user_id) {
        // Floating emoji burst over that user's avatar for 3s.
        const id = `${payload.user_id}-${payload.ts || Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setReactions((rs) => [...rs, { id, user_id: payload.user_id, emoji: payload.emoji }]);
        setTimeout(() => {
          setReactions((rs) => rs.filter((r) => r.id !== id));
        }, 2600);
      }
    });
  }, [subscribe]);

  // send presence update while moving — throttled to ~8Hz so other tabs
  // see smooth motion via CSS interpolation. Debounce would only fire AFTER
  // the user stops which made teammates jump. On any final resting position
  // we also flush after 300ms of no updates.
  const lastSentRef = useRef({ t: 0, x: 0, y: 0 });
  const flushRef = useRef(0);
  useEffect(() => {
    if (!wsSend || !myPos || !hasUserMoved.current) return;
    const now = performance.now();
    const cur = roomAt(rooms, myPos.x, myPos.y);
    if (now - lastSentRef.current.t >= 120) {
      wsSend({ type: "presence.update", x: myPos.x, y: myPos.y, room: cur?.name });
      lastSentRef.current = { t: now, x: myPos.x, y: myPos.y };
    }
    clearTimeout(flushRef.current);
    flushRef.current = setTimeout(() => {
      if (lastSentRef.current.x !== myPos.x || lastSentRef.current.y !== myPos.y) {
        wsSend({ type: "presence.update", x: myPos.x, y: myPos.y, room: cur?.name });
        lastSentRef.current = { t: performance.now(), x: myPos.x, y: myPos.y };
      }
    }, 300);
    return () => clearTimeout(flushRef.current);
  }, [myPos, wsSend, rooms]);

  // Continuous WASD/arrow movement: track pressed keys, tick position in an
  // rAF loop so holding a key walks smoothly instead of tap-tap-tap.
  const keysDown = useRef(new Set());
  const rafRef = useRef(0);
  const lastFrameRef = useRef(0);
  const facingRef = useRef("right"); // "left" | "right" for avatar flip
  const [facing, setFacing] = useState("right");

  useEffect(() => {
    const speedPxPerSec = 220; // ~15.5 px per 70ms frame
    const isEditable = () => {
      const el = document.activeElement;
      return el && (["INPUT", "TEXTAREA"].includes(el.tagName) || el.isContentEditable);
    };
    const onKey = (down) => (e) => {
      if (isEditable()) return;
      const k = e.key.toLowerCase();
      const mapped = {
        arrowup: "up", w: "up",
        arrowdown: "down", s: "down",
        arrowleft: "left", a: "left",
        arrowright: "right", d: "right",
      }[k];
      if (!mapped) return;
      e.preventDefault();
      if (down) keysDown.current.add(mapped);
      else keysDown.current.delete(mapped);
    };
    const kd = onKey(true), ku = onKey(false);
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    const onBlur = () => keysDown.current.clear();
    window.addEventListener("blur", onBlur);

    const tick = (t) => {
      const dt = lastFrameRef.current ? (t - lastFrameRef.current) / 1000 : 0;
      lastFrameRef.current = t;
      const keys = keysDown.current;
      if (keys.size && myPos && !sittingAt) {
        let dx = 0, dy = 0;
        if (keys.has("up")) dy -= 1;
        if (keys.has("down")) dy += 1;
        if (keys.has("left")) dx -= 1;
        if (keys.has("right")) dx += 1;
        // Rotate screen-aligned WASD into iso world axes so W = "up on the
        // canvas" walks visually upward (naturally NW in world coords).
        const { wx, wy } = rotateInputForIso(dx, dy);
        // Normalize so diagonals don't zoom.
        const len = Math.hypot(wx, wy) || 1;
        const step = speedPxPerSec * dt;
        const nx = clamp(myPos.x + (wx / len) * step, 10, MAP_W - 10);
        const ny = clamp(myPos.y + (wy / len) * step, 10, MAP_H - 10);
        // NOTE: wall collision removed intentionally. The seeded office
        // layout has tightly-tiled rooms with no true corridors between
        // them, so any collision rule felt like a cage. Walls stay purely
        // decorative (drawn for iso depth). If we later add real corridor
        // layouts, we can reintroduce collision selectively for privacy
        // rooms (meeting/focus) only.
        hasUserMoved.current = true;
        // Facing: derive from the SCREEN dx so W+D = "up-right" flips
        // avatar to face right; A alone flips it left. Feels natural.
        if (dx !== 0) {
          const dir = dx < 0 ? "left" : "right";
          if (facingRef.current !== dir) {
            facingRef.current = dir;
            setFacing(dir);
          }
        }
        const moved = nx !== myPos.x || ny !== myPos.y;
        if (moved) {
          setMyPos({ x: nx, y: ny });
          if (!isMoving) setIsMoving(true);
        } else if (isMoving) {
          setIsMoving(false);
        }
      } else if (isMoving) {
        // No keys held or currently sitting — stop the walking animation.
        setIsMoving(false);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
      window.removeEventListener("blur", onBlur);
    };
  }, [myPos, rooms, isMoving, sittingAt]);

  const onMapClick = (e) => {
    const rect = mapRef.current.getBoundingClientRect();
    // rect width/height are POST-transform (already includes zoom). Convert
    // back to the underlying iso canvas coord space by scaling.
    const sx = ((e.clientX - rect.left) / rect.width) * ISO_CANVAS_W;
    const sy = ((e.clientY - rect.top) / rect.height) * ISO_CANVAS_H;
    const { x, y } = screenToWorld(sx, sy, ISO_OFFSET_X);
    const targetX = clamp(x, 10, MAP_W - 10);
    const targetY = clamp(y, 10, MAP_H - 10);
    hasUserMoved.current = true;
    if (sittingAt) setSittingAt(null);
    setMyPos({ x: targetX, y: targetY });
  };

  // Click a furniture emoji → sit down. Snaps position and enables the
  // breathing idle animation. Any WASD/click-to-move stands the user up.
  const sitAt = (furnitureId, x, y, emoji) => {
    setSittingAt({ furnitureId, x, y, emoji });
    setMyPos({ x, y });
    hasUserMoved.current = true;
  };

  // Visible users: only include online members on the map. Away users appear muted
  // at their last known position. Offline users are hidden — visible only in People panel.
  const visibleUsers = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      const p = presence?.[m.user_id];
      if (!isEffectivelyOnline(p)) continue;
      const status = p?.status || m.user?.status || "available";
      map.set(m.user_id, {
        user_id: m.user_id,
        id: m.user_id,
        name: m.user?.name,
        // Merge WS presence (fresh avatar fields on save) with membership user record,
        // so the map redraws with the newest avatar immediately after user.updated.
        avatar_url: p?.avatar_url ?? m.user?.avatar_url,
        avatar_type: p?.avatar_type ?? m.user?.avatar_type,
        avatar_seed: p?.avatar_seed ?? m.user?.avatar_seed,
        avatar_config: p?.avatar_config ?? m.user?.avatar_config,
        profile_photo_url: p?.profile_photo_url ?? m.user?.profile_photo_url,
        updated_at: p?.updated_at ?? m.user?.updated_at,
        email: m.user?.email,
        department: m.department,
        title: m.user?.title,
        status,
        online: true,
        x: p?.x ?? 200 + (m.user_id.charCodeAt(0) % 6) * 60,
        y: p?.y ?? 250 + (m.user_id.charCodeAt(1) % 4) * 60,
        room: p?.room,
      });
    }
    if (user && myPos) {
      map.set(user.id, {
        user_id: user.id, id: user.id, name: user.name,
        avatar_url: user.avatar_url,
        avatar_type: user.avatar_type,
        avatar_seed: user.avatar_seed,
        avatar_config: user.avatar_config,
        profile_photo_url: user.profile_photo_url,
        updated_at: user.updated_at,
        email: user.email,
        status: presence?.[user.id]?.status || user.status || "available",
        online: true, x: myPos.x, y: myPos.y, room: roomAt(rooms, myPos.x, myPos.y)?.name,
      });
    }
    return Array.from(map.values());
  }, [members, presence, user, myPos, rooms]);

  const roomOccupants = useMemo(() => {
    const groups = {};
    for (const u of visibleUsers) {
      const r = roomAt(rooms, u.x, u.y);
      if (!r) continue;
      (groups[r.id] || (groups[r.id] = [])).push(u);
    }
    return groups;
  }, [visibleUsers, rooms]);

  const currentRoom = myPos ? roomAt(rooms, myPos.x, myPos.y) : null;

  // Proximity prompt (subtle, dismissable per-user for the session).
  // Skips when the OTHER user OR I am in a "please don't interrupt" state.
  const DO_NOT_DISTURB_SET = useMemo(() => new Set(["focus", "busy", "meeting", "away"]), []);
  useEffect(() => {
    if (!user || !myPos) return;
    const myStatus = presence?.[user.id]?.status;
    if (DO_NOT_DISTURB_SET.has(myStatus)) {
      // Respect my own DND. Never surface prompts while I'm focused.
      if (nearbyPrompt) setNearbyPrompt(null);
      return;
    }
    let nearest = null;
    for (const u of visibleUsers) {
      if (u.user_id === user.id) continue;
      if (DO_NOT_DISTURB_SET.has(u.status)) continue;
      const d = distance(u, myPos);
      if (d < 80 && !dismissedNearby.current.has(u.user_id)) {
        if (!nearest || d < distance(nearest, myPos)) nearest = u;
      }
    }
    setNearbyPrompt(nearest);
  }, [visibleUsers, myPos, user, presence, DO_NOT_DISTURB_SET, nearbyPrompt]);

  const openProfile = (u) => {
    openDrawer("Profile", u.user_id, (
      <div className="p-4">
        <div className="flex items-center gap-3">
          <Avatar user={u} size="xxl" status={u.status} online={u.online} ring/>
          <div>
            <div className="font-semibold text-lg">{u.name}</div>
            <div className="text-sm text-zinc-500">{u.title || u.department || ""}</div>
            <div className="text-xs mt-1 flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[u.status] }}/> {STATUS_LABELS[u.status]}{u.room && ` · ${u.room}`}</div>
          </div>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button size="sm" onClick={async () => {
            const { data } = await api.post("/conversations", { user_ids: [u.user_id] });
            closeDrawer();
            window.location.href = `/app/dm/${data.id}`;
          }} data-testid={`profile-dm-${u.user_id}`}>
            <MessageCircle className="w-4 h-4 mr-1"/> Direct message
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const { data } = await api.post("/calls/start", { room: `dm-${u.user_id}`, kind: "voice" });
            onOpenCall(data); toast.success("Huddle started");
          }} data-testid={`profile-huddle-${u.user_id}`}>
            <Mic className="w-4 h-4 mr-1"/> Start huddle
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            const { data } = await api.post("/calls/start", { room: `dm-${u.user_id}`, kind: "video" });
            onOpenCall(data);
          }}>
            <Video className="w-4 h-4 mr-1"/> Video
          </Button>
        </div>
        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Latest stand-up</div>
          <StandupPreview userId={u.user_id}/>
        </div>
      </div>
    ));
  };

  // ---- Zoom control (mouse-wheel + toolbar) ----------------------------
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 0.5, ZOOM_MAX = 1.8;
  const zoomIn  = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + 0.15).toFixed(2)));
  const zoomOut = () => setZoom((z) => Math.max(ZOOM_MIN, +(z - 0.15).toFixed(2)));
  const zoomReset = () => setZoom(1);
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return; // only zoom with Ctrl/Cmd
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((z) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +(z + delta).toFixed(2))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Find MY assigned seat across all rooms so we can render a "Go to my
  // desk" button + let the sit/claim flow toggle correctly.
  const mySeat = useMemo(() => {
    for (const r of rooms) {
      for (const s of r.seats || []) {
        if (s.assigned_user_id === user?.id) return { room: r, seat: s };
      }
    }
    return null;
  }, [rooms, user?.id]);

  // Snap avatar to my desk and re-center scroll on it.
  const goToMyDesk = () => {
    if (!mySeat) {
      toast.info("You don't have a dedicated desk yet. Click any empty chair to claim one.");
      return;
    }
    const wx = mySeat.room.x + (mySeat.seat.x || 0.5) * mySeat.room.w;
    const wy = mySeat.room.y + (mySeat.seat.y || 0.5) * mySeat.room.h;
    setMyPos({ x: wx, y: wy });
    setSittingAt({ furnitureId: `desk-${mySeat.seat.id}`, x: wx, y: wy, emoji: "🖥️" });
    hasUserMoved.current = true;
    // Scroll the map container so the desk is roughly centered.
    const wrap = mapRef.current?.parentElement;
    if (wrap) {
      const proj = worldToScreen(wx, wy, ISO_OFFSET_X);
      wrap.scrollTo({ left: proj.sx * zoom - wrap.clientWidth / 2, top: proj.sy * zoom - wrap.clientHeight / 2, behavior: "smooth" });
    }
  };

  // Claim a seat by ID. Optimistic update + server sync via WS.
  // Backend will 400 for non-workspace rooms (meeting/focus/etc.) and
  // 403 for manager-locked seats held by someone else — we surface those.
  const claimSeat = async (roomId, seatId, wx, wy, emoji) => {
    try {
      const { data } = await api.post("/office/seats/claim", { room_id: roomId, seat_id: seatId });
      // Apply to local rooms so the chip flips immediately.
      setRooms((prev) => prev.map((r) => {
        if (r.id !== roomId) {
          return { ...r, seats: (r.seats || []).map((s) => s.assigned_user_id === user?.id && !s.manager_locked ? { ...s, assigned_user_id: null } : s) };
        }
        return { ...r, seats: (r.seats || []).map((s) => s.id === seatId ? { ...s, assigned_user_id: user.id, manager_locked: false } : s.assigned_user_id === user?.id && !s.manager_locked ? { ...s, assigned_user_id: null } : s) };
      }));
      // Sit down at the newly-claimed desk.
      if (wx != null && wy != null) {
        setMyPos({ x: wx, y: wy });
        setSittingAt({ furnitureId: `desk-${seatId}`, x: wx, y: wy, emoji: emoji || "🖥️" });
        hasUserMoved.current = true;
      }
      toast.success("Desk claimed — this is now your dedicated spot 🎉");
      return data;
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not claim desk";
      toast.error(msg);
    }
  };

  // Manager-only: assign a specific seat to a specific user. Fires the
  // office.seat_assigned WS event so every teammate's map updates.
  const assignSeat = async (roomId, seatId, targetUserId) => {
    try {
      await api.post("/office/seats/assign", {
        room_id: roomId, seat_id: seatId, user_id: targetUserId || null,
      });
      const { data } = await api.get("/office/layout");
      setRooms(layoutToRooms(data));
      if (targetUserId) {
        const target = members.find((m) => m.id === targetUserId);
        toast.success(target ? `Assigned desk to ${target.name}` : "Desk assigned");
      } else {
        toast.success("Desk unassigned");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update desk assignment");
    }
  };

  // Admin-only: quick resize of a room's seat_count (fires PATCH). We
  // re-fetch the layout after the mutation so seat positions + w/h stay
  // in sync with the server.
  const resizeRoom = async (roomId, newCount) => {
    try {
      await api.patch(`/office/published/rooms/${roomId}/resize`, { seat_count: newCount });
      const { data } = await api.get("/office/layout");
      setRooms(layoutToRooms(data));
      toast.success(`Room resized to ${newCount} seat${newCount === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resize");
    }
  };

  const myRole = useMemo(() => {
    const mine = (memberships || []).find((m) => m.org_id === currentOrg?.id || m.organization?.id === currentOrg?.id);
    return mine?.role || "member";
  }, [memberships, currentOrg?.id]);
  const isAdmin = ["owner", "admin"].includes(myRole);
  // Manager can ALSO assign/unassign dedicated desks (workspace only). Admin
  // inherits everything managers can do. Regular members can only self-claim.
  const isManager = ["owner", "admin", "manager"].includes(myRole);

  // Plan seat cap for the current org — drives slider max + upgrade tooltip.
  // Falls back to 5 (Free) if the API hasn't responded yet. Hot-swappable
  // once the org.plan changes via billing (Team = 100 seats/room).
  const [planLimits, setPlanLimits] = useState({ seat_cap_per_room: { free: 5, trial: 100, team: 100 }, default_cap: 5 });
  useEffect(() => {
    let alive = true;
    api.get("/plans/limits")
      .then((r) => { if (alive) setPlanLimits(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const currentPlan = (currentOrg?.plan || "free").toLowerCase();
  const seatCap = planLimits.seat_cap_per_room?.[currentPlan] ?? planLimits.default_cap ?? 5;
  const isFreePlan = currentPlan === "free";

  const onlineCount = visibleUsers.length;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="h-14 border-b border-[var(--border-subtle)] bg-white flex items-center justify-between px-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Virtual office</div>
          <h1 className="text-base font-semibold">
            {currentOrg?.name}
            {currentRoom && <span className="ml-2 text-sm font-normal text-zinc-500">· You are in <b className="text-[var(--brand-accent)]">{currentRoom.name}</b></span>}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className="hidden lg:inline">Move with WASD/arrows or tap the floor</span>
          {/* Zoom controls */}
          <div className="flex items-center gap-0.5 border border-zinc-200 rounded-full p-0.5" data-testid="office-zoom-controls">
            <button onClick={zoomOut} aria-label="Zoom out" data-testid="office-zoom-out"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-zinc-100 disabled:opacity-40"
              disabled={zoom <= 0.5}
            ><ZoomOut className="w-3.5 h-3.5"/></button>
            <button onClick={zoomReset} aria-label="Reset zoom" data-testid="office-zoom-reset"
              className="w-10 text-[11px] font-semibold tabular-nums hover:text-[var(--brand-accent)]"
            >{Math.round(zoom * 100)}%</button>
            <button onClick={zoomIn} aria-label="Zoom in" data-testid="office-zoom-in"
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-zinc-100 disabled:opacity-40"
              disabled={zoom >= 1.8}
            ><ZoomIn className="w-3.5 h-3.5"/></button>
          </div>
          {/* Go to my desk */}
          <Button size="sm" variant="outline" onClick={goToMyDesk} data-testid="office-go-my-desk">
            <Home className="w-4 h-4 mr-1"/> My desk
          </Button>
          <Button size="sm" variant="outline" onClick={onOpenPeople} data-testid="office-open-people">
            <Users className="w-4 h-4 mr-1"/> People
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto scroll-thin">
        <div className="min-w-fit p-6 md:p-10 flex items-center justify-center">
          <div
            ref={mapRef}
            onClick={onMapClick}
            className="relative rounded-3xl shadow-xl overflow-visible cursor-pointer iso-bg"
            style={{
              width: ISO_CANVAS_W, height: ISO_CANVAS_H,
              transform: `scale(${zoom})`,
              transformOrigin: "0 0",
              marginBottom: (zoom - 1) * ISO_CANVAS_H,
              marginRight: (zoom - 1) * ISO_CANVAS_W,
            }}
            data-testid="office-map"
            data-zoom={zoom}
            role="application"
            aria-label="Virtual office map"
          >
            {/* Iso floor patterns for each room (sorted back-to-front so nearer
                walls occlude farther ones — classic painter's algorithm). */}
            {[...rooms]
              .sort((a, b) => (a.x + a.y) - (b.x + b.y))
              .map((r) => (
                <IsoRoom
                  key={r.id}
                  r={r}
                  occupants={roomOccupants[r.id] || []}
                  isMine={currentRoom?.id === r.id}
                  onSit={sitAt}
                  onClaim={claimSeat}
                  onAssign={assignSeat}
                  isAdmin={isAdmin}
                  isManager={isManager}
                  onResize={resizeRoom}
                  seatCap={seatCap}
                  isFreePlan={isFreePlan}
                  members={members}
                  myId={user?.id}
                  onOpenCall={onOpenCall}
                />
              ))}

            {/* Avatars — sorted by world Y so the "front" avatar renders on top of ones behind. */}
            {[...visibleUsers]
              .sort((a, b) => (a.x + a.y) - (b.x + b.y))
              .map((u) => {
                const me = u.user_id === user?.id;
                const muted = u.status === "away";
                const firstName = (u.name || "").split(" ")[0] || u.name || "";
                const myReactions = reactions.filter((r) => r.user_id === u.user_id);
                const { sx, sy } = worldToScreen(u.x, u.y, ISO_OFFSET_X);
                return (
                  <div key={u.user_id}
                    onMouseEnter={() => setHoverUser(u.user_id)}
                    onMouseLeave={() => setHoverUser(null)}
                    onClick={(e) => { e.stopPropagation(); if (!me) openProfile(u); }}
                    data-testid={`avatar-${u.user_id}`}
                    data-moving={me && isMoving ? "true" : "false"}
                    data-sitting={me && sittingAt ? "true" : "false"}
                    className={`absolute transition-[left,top] duration-150 ease-out flex flex-col items-center ${me && isMoving ? "avatar-walking" : ""} ${me && sittingAt ? "avatar-sitting" : ""}`}
                    style={{ left: sx - 20, top: sy - 46, zIndex: Math.floor(u.x + u.y) }}
                  >
                    {/* Soft iso ground shadow — sold under the avatar */}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-2 rounded-[50%] bg-black/25 blur-[2px]"/>
                    <div
                      className="avatar-body"
                      style={me && facing === "left" ? { transform: "scaleX(-1)" } : undefined}
                    >
                      <Avatar user={u} size="lg" status={u.status} online={u.online} ring={me} muted={muted} showStatus meetingBadge className={me ? "pulse-ring" : ""}/>
                    </div>
                    {/* Floating emoji burst — one per active reaction */}
                    {myReactions.map((r, i) => (
                      <div
                        key={r.id}
                        className="absolute left-1/2 -translate-x-1/2 -top-6 text-2xl pointer-events-none office-reaction-burst"
                        style={{ animationDelay: `${i * 60}ms` }}
                        data-testid={`office-reaction-${u.user_id}`}
                      >{r.emoji}</div>
                    ))}
                    {/* Always-on Gather-Town-style first-name label */}
                    <div
                      className={`mt-1 whitespace-nowrap text-[11px] font-semibold rounded-full px-2 py-0.5 pointer-events-none shadow-sm border border-white ${
                        me
                          ? "bg-[var(--brand-accent)] text-white"
                          : muted
                          ? "bg-white/85 text-zinc-500"
                          : "bg-white/95 text-zinc-800"
                      }`}
                      data-testid={`avatar-name-${u.user_id}`}
                    >
                      {firstName}
                    </div>
                    {/* Full name on hover */}
                    {hoverUser === u.user_id && !me && (
                      <div className="absolute left-1/2 -translate-x-1/2 -top-8 whitespace-nowrap text-[11px] bg-black/85 text-white px-2 py-0.5 rounded pointer-events-none">
                        {u.name}
                      </div>
                    )}
                  </div>
                );
              })}

            {nearbyPrompt && myPos && !currentRoom?.kind?.includes("meeting") && (() => {
              const p1 = worldToScreen(nearbyPrompt.x, nearbyPrompt.y, ISO_OFFSET_X);
              const p2 = worldToScreen(myPos.x, myPos.y, ISO_OFFSET_X);
              const midX = (p1.sx + p2.sx) / 2;
              const midY = Math.min(p1.sy, p2.sy) - 60;
              return (
                <div className="absolute bg-white shadow-xl border-2 border-[var(--brand-accent)]/40 rounded-full px-3 py-1.5 text-xs flex items-center gap-2 z-50"
                  style={{ left: midX - 100, top: midY }}>
                  <Sparkles className="w-3.5 h-3.5 text-[var(--brand-accent)]"/>
                  <span>{nearbyPrompt.name.split(" ")[0]} is nearby.</span>
                  <button className="text-[var(--brand-accent)] font-semibold" onClick={async (e) => { e.stopPropagation(); const { data } = await api.post("/calls/start", { room: `nearby-${nearbyPrompt.user_id}`, kind: "voice" }); onOpenCall(data); }}>Start huddle</button>
                  <button className="text-zinc-400 hover:text-zinc-700" onClick={(e) => { e.stopPropagation(); dismissedNearby.current.add(nearbyPrompt.user_id); setNearbyPrompt(null); }}>Dismiss</button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Bottom presence bar */}
      <div className="h-16 border-t border-[var(--border-subtle)] bg-white flex items-center px-4 md:px-6 gap-3 overflow-x-auto scroll-thin">
        <button onClick={onOpenPeople} data-testid="presence-bar-people"
          className="flex items-center gap-2 shrink-0 pr-3 border-r border-zinc-200 hover:opacity-90">
          <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">{onlineCount}</div>
          <div className="leading-tight text-left">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Online</div>
            <div className="text-sm font-medium">People</div>
          </div>
        </button>
        {/* Quick-react emoji bar — floats over avatars for everyone */}
        <div className="flex items-center gap-1 shrink-0 pr-3 border-r border-zinc-200" data-testid="office-reactions-bar">
          {["👋", "🎉", "👍", "❤️", "🚀"].map((emoji) => (
            <button
              key={emoji}
              onClick={() => sendReaction(emoji)}
              disabled={!connected}
              data-testid={`office-react-${emoji}`}
              aria-label={`React with ${emoji}`}
              title={connected ? `React with ${emoji}` : "Connecting…"}
              className="w-8 h-8 rounded-full hover:bg-zinc-100 active:scale-90 transition text-lg leading-none flex items-center justify-center disabled:opacity-40 disabled:cursor-wait"
            >{emoji}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {visibleUsers.slice(0, 12).map((u) => (
            <button key={u.user_id} onClick={() => u.user_id !== user?.id && openProfile(u)} data-testid={`online-${u.user_id}`}
              className="shrink-0 hover:scale-110 transition">
              <Avatar user={u} size="md" status={u.status} online={u.online} muted={u.status === "away"} title={`${u.name} · ${STATUS_LABELS[u.status]}`}/>
            </button>
          ))}
          {visibleUsers.length > 12 && (
            <button onClick={onOpenPeople} className="w-8 h-8 rounded-full bg-zinc-100 text-xs text-zinc-600 flex items-center justify-center">+{visibleUsers.length - 12}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const SITTABLE_EMOJI = new Set(["🖥️", "💻", "🪑", "🛋️", "🎧", "🎮", "📺"]);

/**
 * Decorative furniture per room type — plants, TVs, coffee machines,
 * tables, non-seat props. ACTUAL seats come from `r.seats` (backend-
 * generated with unit coords) and are rendered separately with proper
 * desk + monitor + chair combos plus assigned-user chips.
 *
 * Returns items with WORLD coords. Each item is one of:
 *   { kind: "desk"|"table", x, y, w, h }   — SVG iso rectangle
 *   { kind: "emoji", emoji, x, y }         — decorative emoji
 */
function furnitureFor(r) {
  const items = [];
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const push = (obj) => items.push(obj);

  switch (r.kind) {
    case "meeting": {
      // Central conference table — chairs come from r.seats around it.
      const tw = Math.min(r.w * 0.55, 240);
      const th = Math.min(r.h * 0.35, 80);
      push({ kind: "table", x: cx - tw / 2, y: cy - th / 2, w: tw, h: th });
      push({ kind: "emoji", emoji: "📺", x: cx, y: r.y + 32 });
      push({ kind: "emoji", emoji: "🌿", x: r.x + 30, y: r.y + r.h - 40 });
      break;
    }
    case "workspace":
      push({ kind: "emoji", emoji: "☕", x: r.x + r.w - 34, y: r.y + 30 });
      push({ kind: "emoji", emoji: "🌿", x: r.x + 30, y: r.y + 30 });
      break;
    case "focus":
      push({ kind: "emoji", emoji: "🎧", x: r.x + 40, y: r.y + r.h - 40 });
      push({ kind: "emoji", emoji: "🕯️", x: r.x + r.w - 34, y: r.y + r.h - 40 });
      push({ kind: "emoji", emoji: "📚", x: r.x + r.w - 34, y: r.y + 40 });
      break;
    case "reception":
      push({ kind: "desk", x: cx - Math.min(r.w * 0.4, 100), y: r.y + 40, w: Math.min(r.w * 0.8, 200), h: 28 });
      push({ kind: "emoji", emoji: "📋", x: cx - 30, y: r.y + 46 });
      push({ kind: "emoji", emoji: "☕", x: cx + 30, y: r.y + 46 });
      push({ kind: "emoji", emoji: "🌿", x: r.x + 30, y: r.y + r.h - 30 });
      push({ kind: "emoji", emoji: "🌱", x: r.x + r.w - 40, y: r.y + r.h - 30 });
      break;
    case "breakout":
    case "lounge": {
      const tw = Math.min(90, r.w * 0.4), th = Math.min(50, r.h * 0.3);
      push({ kind: "table", x: cx - tw / 2, y: cy - th / 2, w: tw, h: th });
      push({ kind: "emoji", emoji: "☕", x: cx - 10, y: cy - 6 });
      push({ kind: "emoji", emoji: "🍪", x: cx + 12, y: cy - 6 });
      push({ kind: "emoji", emoji: "🌿", x: r.x + 30, y: r.y + 30 });
      push({ kind: "emoji", emoji: "🌵", x: r.x + r.w - 34, y: r.y + 30 });
      break;
    }
    case "ops": {
      push({ kind: "emoji", emoji: "📊", x: r.x + r.w - 34, y: r.y + r.h - 40 });
      push({ kind: "emoji", emoji: "⚡", x: r.x + 30, y: r.y + r.h - 40 });
      break;
    }
    default:
      break;
  }
  return items;
}

function IsoRoom({ r, occupants, isMine, onSit, onClaim, onAssign, isAdmin, isManager, onResize, seatCap, isFreePlan, members, myId, onOpenCall }) {
  const theme = ROOM_THEME[r.kind] || ROOM_THEME.workspace;
  const inMeeting = r.kind === "meeting" && occupants.length > 0;
  // Room types that support dedicated desks — mirrors the backend
  // DEDICATED_DESK_ROOM_TYPES set. Meeting / focus / reception / break /
  // lounge chairs are shared and cannot be claimed or assigned.
  const workspaceLike = r.type === "open_workspace" || r.type === "department" || r.type === "private";

  // Auto-huddle threshold: when 2+ people share a meeting room, offer
  // a one-click voice huddle so the meeting actually happens.
  const showAutoHuddle = r.kind === "meeting" && occupants.length >= 2 && !!onOpenCall;
  const iAmInThisRoom = occupants.some((u) => u.user_id === myId);

  // Four corners of the room in world → screen space.
  const nw = worldToScreen(r.x, r.y, ISO_OFFSET_X);
  const ne = worldToScreen(r.x + r.w, r.y, ISO_OFFSET_X);
  const sw = worldToScreen(r.x, r.y + r.h, ISO_OFFSET_X);
  const se = worldToScreen(r.x + r.w, r.y + r.h, ISO_OFFSET_X);

  // Floor is a polygon in the outer SVG covering the whole canvas.
  // We overlay walls as separate divs to keep depth ordering clean.
  const floorPath = `${nw.sx},${nw.sy} ${ne.sx},${ne.sy} ${se.sx},${se.sy} ${sw.sx},${sw.sy}`;
  // Label at the back-most corner (nw) so it doesn't overlap furniture.
  const labelX = (nw.sx + ne.sx) / 2;
  const labelY = (nw.sy + ne.sy) / 2 - 26;

  // Doorway on the FRONT (south, visible) wall — visible gap in the wall
  // polygon so the room communicates "walk in here". Wider (140 world
  // units) than the collision default so it's obvious to the eye.
  const DOOR_W = 140;
  const doorCx = r.door?.cx != null ? r.door.cx : r.x + r.w / 2;
  const doorLeftX  = Math.max(r.x + 12, doorCx - DOOR_W / 2);
  const doorRightX = Math.min(r.x + r.w - 12, doorCx + DOOR_W / 2);
  // South wall runs from SW → SE. We slice out the door segment.
  const dl = worldToScreen(doorLeftX, r.y + r.h, ISO_OFFSET_X);
  const dr = worldToScreen(doorRightX, r.y + r.h, ISO_OFFSET_X);
  const southLeft = `${sw.sx},${sw.sy} ${dl.sx},${dl.sy} ${dl.sx},${dl.sy - WALL_HEIGHT_PX} ${sw.sx},${sw.sy - WALL_HEIGHT_PX}`;
  const southRight = `${dr.sx},${dr.sy} ${se.sx},${se.sy} ${se.sx},${se.sy - WALL_HEIGHT_PX} ${dr.sx},${dr.sy - WALL_HEIGHT_PX}`;

  // Furniture layout tied to the room's kind — meja/kursi/etc. that
  // visually communicate what the room is for.
  const items = furnitureFor(r);
  // Split into two render lists: SVG shapes (desks/tables) drawn inside
  // the SVG at floor-level, and emojis rendered as absolute-positioned
  // DOM nodes above the floor (so they can be clickable + have hover).
  const svgItems = items.filter((it) => it.kind === "desk" || it.kind === "table");
  const domItems = items.filter((it) => it.kind === "chair" || it.kind === "emoji");

  return (
    <>
      {/* Floor diamond + walls + inline desks/tables */}
      <svg
        className="absolute pointer-events-none"
        style={{ left: 0, top: 0, width: "100%", height: "100%", overflow: "visible" }}
        aria-hidden="true"
      >
        <polygon
          points={floorPath}
          fill={theme.floor}
          stroke={isMine ? "var(--brand-accent)" : "rgba(0,0,0,0.06)"}
          strokeWidth={isMine ? 3 : 1.5}
        />
        {/* Right wall (E face) — solid */}
        <polygon
          points={`${ne.sx},${ne.sy} ${se.sx},${se.sy} ${se.sx},${se.sy - WALL_HEIGHT_PX} ${ne.sx},${ne.sy - WALL_HEIGHT_PX}`}
          fill={theme.wall}
          stroke="rgba(0,0,0,0.12)"
        />
        {/* Front wall (S face) — split into TWO segments with a door gap in the middle */}
        <polygon points={southLeft}  fill={theme.wall} fillOpacity="0.85" stroke="rgba(0,0,0,0.12)"/>
        <polygon points={southRight} fill={theme.wall} fillOpacity="0.85" stroke="rgba(0,0,0,0.12)"/>
        {/* Doorway threshold */}
        <polygon
          points={`${dl.sx},${dl.sy} ${dr.sx},${dr.sy} ${dr.sx + 2},${dr.sy + 3} ${dl.sx - 2},${dl.sy + 3}`}
          fill="rgba(60, 40, 20, 0.35)"
        />
        <line x1={dl.sx} y1={dl.sy} x2={dl.sx} y2={dl.sy - WALL_HEIGHT_PX}
              stroke={theme.accent} strokeWidth="2.5" strokeLinecap="round"/>
        <line x1={dr.sx} y1={dr.sy} x2={dr.sx} y2={dr.sy - WALL_HEIGHT_PX}
              stroke={theme.accent} strokeWidth="2.5" strokeLinecap="round"/>

        {/* Desks & tables — iso-projected rounded rectangles rendered at
            floor level with a slight shadow underneath. */}
        {svgItems.map((it, i) => {
          const c1 = worldToScreen(it.x, it.y, ISO_OFFSET_X);
          const c2 = worldToScreen(it.x + it.w, it.y, ISO_OFFSET_X);
          const c3 = worldToScreen(it.x + it.w, it.y + it.h, ISO_OFFSET_X);
          const c4 = worldToScreen(it.x, it.y + it.h, ISO_OFFSET_X);
          const fill = it.kind === "table" ? "#B08968" : "#D4A574";
          const top = it.kind === "table" ? "#C89A73" : "#E5B98F";
          return (
            <g key={`f-${i}`}>
              {/* soft ground shadow */}
              <polygon
                points={`${c1.sx},${c1.sy + 4} ${c2.sx},${c2.sy + 4} ${c3.sx},${c3.sy + 4} ${c4.sx},${c4.sy + 4}`}
                fill="rgba(0,0,0,0.14)"
              />
              {/* front-facing side (depth) */}
              <polygon
                points={`${c1.sx},${c1.sy} ${c2.sx},${c2.sy} ${c3.sx},${c3.sy} ${c4.sx},${c4.sy}`}
                fill={fill}
                stroke="rgba(0,0,0,0.15)"
                strokeWidth="1"
              />
              {/* subtle top highlight */}
              <polygon
                points={`${c1.sx + 2},${c1.sy + 1} ${c2.sx - 2},${c2.sy + 1} ${c2.sx - 6},${c2.sy + 4} ${c1.sx + 6},${c1.sy + 4}`}
                fill={top}
              />
            </g>
          );
        })}
      </svg>

      {/* Room label as a floating cloud pill */}
      <div
        className="absolute rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase shadow-sm border-2 border-white flex items-center gap-1.5"
        onClick={(e) => e.stopPropagation()}
        style={{
          left: labelX - 60, top: labelY,
          background: theme.accent, color: "white",
          zIndex: Math.floor(r.x + r.y) - 1,
        }}
      >
        <span className="truncate max-w-[100px]">{r.name}</span>
        {occupants.length > 0 && (
          <span className="w-4 h-4 rounded-full bg-white/25 text-[9px] font-bold flex items-center justify-center">{occupants.length}</span>
        )}
        {inMeeting && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>}
        {/* Admin-only quick resize gear */}
        {isAdmin && onResize && (
          <RoomResizePopover r={r} onResize={onResize} seatCap={seatCap} isFreePlan={isFreePlan}/>
        )}
      </div>

      {/* Meeting Auto-Huddle CTA — appears when 2+ people gather at the
          conference table. Anchored to the center of the room, above the
          table, so the "everyone's already here, let's actually talk" moment
          is one obvious click away. */}
      {showAutoHuddle && (() => {
        const cx = r.x + r.w / 2;
        const cy = r.y + r.h / 2 - r.h * 0.15;
        const p = worldToScreen(cx, cy, ISO_OFFSET_X);
        return (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const { data } = await api.post("/calls/start", {
                  room: `office-meeting-${r.id}`,
                  kind: "voice",
                });
                onOpenCall(data);
                toast.success("Table huddle started — teammates in this room can join");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Could not start huddle");
              }
            }}
            data-testid={`meeting-auto-huddle-${r.id}`}
            className="absolute -translate-x-1/2 -translate-y-full rounded-full pl-2 pr-3 py-1.5 text-[11px] font-semibold shadow-lg border-2 border-white flex items-center gap-1.5 bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition"
            style={{
              left: p.sx, top: p.sy - 8,
              zIndex: Math.floor(cx + cy) + 100,
            }}
            title={iAmInThisRoom
              ? `${occupants.length} people at this table — start a voice huddle`
              : `${occupants.length} people meeting at this table`}
          >
            <Mic className="w-3.5 h-3.5"/>
            <span>Start table huddle</span>
            <span className="ml-1 w-4 h-4 rounded-full bg-white/25 text-[10px] font-bold flex items-center justify-center">{occupants.length}</span>
          </button>
        );
      })()}

      {/* Backend-driven SEATS — one desk + monitor + chair per seat.
          Behavior branches by room type + seat state:
            • Workspace rooms: click empty → claim / click own → sit
              (manager can also open an Assign popover per seat).
            • Non-workspace rooms (meeting, focus, reception, break,
              lounge…): chairs are shared — click just sits you there,
              no claim/assign action available. */}
      {(r.seats || []).map((seat) => {
        const wx = r.x + (seat.x || 0.5) * r.w;
        const wy = r.y + (seat.y || 0.5) * r.h;
        const proj = worldToScreen(wx, wy, ISO_OFFSET_X);
        const isMineDesk = seat.assigned_user_id === myId;
        const heldBy = seat.assigned_user_id
          ? members.find((m) => m.id === seat.assigned_user_id)
          : null;
        const managerLocked = !!seat.manager_locked;
        // A seat is self-claimable when: workspace-type room, and either
        // unassigned (any user) or already assigned to me. Manager-locked
        // seats held by SOMEONE ELSE are strictly off-limits.
        const claimableByMe =
          workspaceLike &&
          (!seat.assigned_user_id ? !managerLocked : isMineDesk);
        // Chair vs desk offset: chair sits 24 world px "south" of the desk
        // so the avatar naturally lands at the chair when claiming.
        const chairY = wy + Math.min(24, r.h * 0.08);
        const chairProj = worldToScreen(wx, chairY, ISO_OFFSET_X);
        const emoji = r.kind === "meeting" || r.kind === "reception" ? "🪑" : "🖥️";
        const seatTitle = (() => {
          if (!workspaceLike) return "Shared chair — click to sit";
          if (heldBy) {
            if (isMineDesk) return "Your desk — click to sit";
            return managerLocked
              ? `${heldBy.name}'s desk (assigned by a manager)`
              : `${heldBy.name}'s desk`;
          }
          return "Empty desk — click to claim as yours";
        })();
        return (
          <React.Fragment key={seat.id}>
            {/* Desk top (thin iso plate under the monitor) */}
            {r.kind === "workspace" && (
              <svg
                className="absolute pointer-events-none"
                style={{ left: proj.sx - 18, top: proj.sy - 6, width: 36, height: 14, overflow: "visible" }}
                aria-hidden="true"
              >
                <polygon points="4,2 32,2 30,10 6,10" fill="#D4A574" stroke="rgba(0,0,0,0.15)"/>
                <polygon points="4,2 32,2 34,4 6,4" fill="#E5B98F"/>
              </svg>
            )}
            {/* The clickable seat "focus point" — a monitor or chair emoji */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (workspaceLike && claimableByMe) {
                  onClaim && onClaim(r.id, seat.id, wx, chairY, emoji);
                } else if (workspaceLike && managerLocked && !isMineDesk) {
                  // Manager-locked seat held by someone else — no-op click,
                  // but tell the user WHY it's not claimable.
                  if (heldBy) toast.info(`${heldBy.name}'s desk is manager-assigned. Ask an admin to reassign it.`);
                } else {
                  // Non-workspace room: click just sits you here.
                  onSit && onSit(`chair-${seat.id}`, wx, chairY, emoji);
                }
              }}
              data-testid={`seat-${seat.id}`}
              data-claimed={seat.assigned_user_id ? "true" : "false"}
              data-mine={isMineDesk ? "true" : "false"}
              data-manager-locked={managerLocked ? "true" : "false"}
              data-workspace-like={workspaceLike ? "true" : "false"}
              className={`absolute text-xl select-none cursor-pointer hover:scale-110 hover:-translate-y-0.5 transition-transform ${isMineDesk ? "office-seat-mine" : ""}`}
              style={{
                left: proj.sx - 12, top: proj.sy - 20,
                zIndex: Math.floor(wx + wy),
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))",
              }}
              title={seatTitle}
            >{emoji}</div>
            {/* Chair emoji at the "sitting" position below the desk */}
            <div
              className="absolute text-lg select-none pointer-events-none"
              style={{
                left: chairProj.sx - 10, top: chairProj.sy - 16,
                zIndex: Math.floor(wx + chairY),
                filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))",
              }}
            >🪑</div>
            {/* Name chip on assigned desks — mini avatar + initials */}
            {heldBy && (
              <div
                className={`absolute pointer-events-none rounded-full px-1.5 py-0.5 text-[9px] font-semibold shadow-sm border flex items-center gap-0.5 ${isMineDesk ? "bg-[var(--brand-accent)] text-white border-white" : "bg-white/95 text-zinc-700 border-white"}`}
                style={{
                  left: proj.sx - 24, top: proj.sy - 34,
                  zIndex: Math.floor(wx + wy) + 1,
                  maxWidth: 72,
                }}
              >
                {managerLocked && (
                  <span
                    className={isMineDesk ? "text-white/90" : "text-amber-600"}
                    aria-label="Manager assigned"
                    title="Manager assigned"
                  >🔒</span>
                )}
                <span className="truncate">{(heldBy.name || "").split(" ")[0]}</span>
              </div>
            )}
            {/* Manager-only assign gear — WORKSPACE seats only. Lives at the
                top-right of the desk emoji so it doesn't overlap the name chip. */}
            {isManager && workspaceLike && onAssign && (
              <SeatAssignPopover
                room={r}
                seat={seat}
                members={members}
                heldBy={heldBy}
                myId={myId}
                onAssign={onAssign}
                anchor={{ left: proj.sx + 6, top: proj.sy - 24 }}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* Decorative props (tables, plants, TV, coffee) — non-seat items */}
      {domItems.map((it, i) => {
        const emoji = it.emoji;
        const proj = worldToScreen(it.x, it.y, ISO_OFFSET_X);
        return (
          <div
            key={`${r.id}-deco-${i}`}
            className="absolute text-xl select-none pointer-events-none"
            style={{
              left: proj.sx - 12, top: proj.sy - 20,
              zIndex: Math.floor(it.x + it.y),
              filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.2))",
            }}
          >{emoji}</div>
        );
      })}
    </>
  );
}

// Quick capacity resize control on room labels — admins only. The slider
// max reflects the org's plan cap (Free = 5, Team = 100); Free plans see
// an upgrade hint when the slider tops out.
function RoomResizePopover({ r, onResize, seatCap = 100, isFreePlan = false }) {
  const current = r.seats?.length ?? r.seat_count ?? 0;
  const [count, setCount] = React.useState(current);
  React.useEffect(() => { setCount(r.seats?.length ?? r.seat_count ?? 0); }, [r.seats?.length, r.seat_count]);
  const atCap = count >= seatCap;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-testid={`room-resize-${r.id}`}
          aria-label="Adjust room capacity"
          className="ml-1 w-4 h-4 rounded-full bg-white/25 hover:bg-white/40 flex items-center justify-center"
        ><Settings2 className="w-2.5 h-2.5"/></button>
      </PopoverTrigger>
      <PopoverContent onClick={(e) => e.stopPropagation()} align="start" className="w-64 p-3" data-testid={`resize-panel-${r.id}`}>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Capacity</div>
        <div className="font-semibold text-sm mb-2">{r.name}</div>
        <div className="flex items-center gap-2">
          <input
            type="range" min={0} max={seatCap} step={1}
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
            data-testid={`resize-slider-${r.id}`}
            data-max={seatCap}
            className="flex-1"
          />
          <span className="w-10 text-center text-sm font-semibold tabular-nums">{count}</span>
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">
          Room auto-resizes to fit {count} seat{count === 1 ? "" : "s"} (max {seatCap} on your plan).
        </div>
        {isFreePlan && atCap && (
          <div
            className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2 leading-snug"
            data-testid={`resize-upgrade-hint-${r.id}`}
          >
            Free plan tops out at {seatCap} seats per room. Upgrade to Team to unlock up to 100.
          </div>
        )}
        <Button
          size="sm"
          onClick={() => onResize(r.id, count)}
          data-testid={`resize-apply-${r.id}`}
          className="w-full mt-3"
        >Apply</Button>
      </PopoverContent>
    </Popover>
  );
}

// Manager-only per-seat assign popover — WORKSPACE seats only. Lets an
// owner/admin/manager pick a member to permanently sit here, or unassign
// an existing dedicated desk. Backend enforces same rules (routes_office.py).
function SeatAssignPopover({ room, seat, members, heldBy, myId, onAssign, anchor }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = members || [];
    if (q) {
      list = list.filter((m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      );
    }
    return list.slice(0, 8);
  }, [members, query]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          data-testid={`seat-assign-btn-${seat.id}`}
          aria-label="Assign this desk to a member"
          title={heldBy ? `Currently assigned to ${heldBy.name}` : "Assign this desk"}
          className="absolute w-5 h-5 rounded-full bg-white border border-zinc-300 shadow-sm text-[10px] font-bold text-zinc-600 hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)] flex items-center justify-center"
          style={{
            left: anchor.left,
            top: anchor.top,
            zIndex: Math.floor((room.x + seat.x * room.w) + (room.y + seat.y * room.h)) + 2,
          }}
        >+</button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={(e) => e.stopPropagation()}
        className="w-64 p-3"
        data-testid={`seat-assign-panel-${seat.id}`}
      >
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Dedicated desk</div>
        <div className="font-semibold text-sm mb-2">{room.name}</div>
        {heldBy ? (
          <div className="mb-2 text-[11px] text-zinc-600">
            Currently assigned to <b>{heldBy.name}</b>
            {seat.manager_locked && (
              <span className="ml-1 inline-flex items-center gap-0.5 text-amber-600">
                🔒 manager-locked
              </span>
            )}
          </div>
        ) : (
          <div className="mb-2 text-[11px] text-zinc-500">Currently unassigned — hot-desk mode.</div>
        )}
        <input
          type="text"
          placeholder="Search teammates…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid={`seat-assign-search-${seat.id}`}
          className="w-full text-xs px-2 py-1.5 rounded-md border border-zinc-200 focus:outline-none focus:border-[var(--brand-accent)] mb-2"
        />
        <div className="max-h-40 overflow-y-auto scroll-thin space-y-1">
          {filtered.map((m) => (
            <button
              key={m.id}
              onClick={() => { onAssign(room.id, seat.id, m.id); setOpen(false); setQuery(""); }}
              data-testid={`seat-assign-pick-${seat.id}-${m.id}`}
              className={`w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-zinc-100 flex items-center justify-between ${m.id === heldBy?.id ? "bg-zinc-50" : ""}`}
            >
              <span className="truncate">{m.name}{m.id === myId ? " (you)" : ""}</span>
              {m.id === heldBy?.id && <span className="text-[10px] text-emerald-600">✓ assigned</span>}
            </button>
          ))}
          {!filtered.length && (
            <div className="text-[11px] text-zinc-400 text-center py-2">No teammates match</div>
          )}
        </div>
        {heldBy && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { onAssign(room.id, seat.id, null); setOpen(false); }}
            data-testid={`seat-unassign-${seat.id}`}
            className="w-full mt-2 text-xs"
          >Unassign desk (hot-desk)</Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function StandupPreview({ userId }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/standups", { params: { user_id: userId } }).then((r) => setS(r.data[0]));
  }, [userId]);
  if (!s) return <div className="text-xs text-zinc-400">No recent stand-up.</div>;
  return (
    <div className="text-sm space-y-2 bg-zinc-50 border rounded-md p-3">
      <div><span className="text-xs uppercase text-zinc-500">Yesterday:</span> {s.yesterday}</div>
      <div><span className="text-xs uppercase text-zinc-500">Today:</span> {s.today}</div>
      {s.blockers && <div><span className="text-xs uppercase text-zinc-500">Blockers:</span> {s.blockers}</div>}
    </div>
  );
}
